import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IError } from '@axmit/error-helper';
import { AppDispatch } from 'app/store';
import {
  IPurchaseOrderCollection,
  IPurchaseOrderCollectionParams,
  IPurchaseOrderCreatePayload,
  IPurchaseOrderModel,
  IPurchaseOrderModelParams,
  IPurchaseOrderOutstandingPayablesModel,
  IPurchaseOrderRecoBasisFilters,
  IPurchaseOrderRecoBasisModel,
  IPurchaseOrderRecoModel,
  IPurchaseOrderRecoParams,
  IPurchaseOrderRecoProduct,
  IPurchaseOrderRecoStoreCollection,
  IPurchaseOrderStoreCollection,
} from 'entities/PurchaseOrder/PurchaseOrder.models';
import { purchaseOrderTransport } from 'entities/PurchaseOrder/PurchaseOrder.transport';

export interface IState {
  // purchase order collection
  purchaseOrderCollectionLoading: boolean;
  purchaseOrderCollection: IPurchaseOrderCollection | null;
  purchaseOrderCollectionError: IError | null;
  // purchase order model
  purchaseOrderModelLoading: boolean;
  purchaseOrderModel: IPurchaseOrderModel | null;
  purchaseOrderModelError: IError | null;
  // purchase order store collection
  purchaseOrderStoreCollectionLoading: boolean;
  purchaseOrderStoreCollection: IPurchaseOrderStoreCollection | null;
  purchaseOrderStoreCollectionError: IError | null;
  // purchase order reco model
  purchaseOrderRecoModelLoading: boolean;
  purchaseOrderRecoModel: IPurchaseOrderRecoModel | null;
  purchaseOrderRecoModelError: IError | null;
  purchaseOrderRecoModelProducts: IPurchaseOrderRecoProduct[];
  // purchase order reco basis model
  purchaseOrderRecoBasisModelLoading: boolean;
  purchaseOrderRecoBasisModel: IPurchaseOrderRecoBasisModel | null;
  purchaseOrderRecoBasisModelError: IError | null;
  // purchase order outstanding payables model
  purchaseOrderOutstandingPayablesModelLoading: boolean;
  purchaseOrderOutstandingPayablesModel: IPurchaseOrderOutstandingPayablesModel | null;
  purchaseOrderOutstandingPayablesModelError: IError | null;
  // purchase order reco store collection
  purchaseOrderRecoStoreCollectionLoading: boolean;
  purchaseOrderRecoStoreCollection: IPurchaseOrderRecoStoreCollection | null;
  purchaseOrderRecoStoreCollectionError: IError | null;
}

const initialState: IState = {
  // purchase order collection
  purchaseOrderCollectionLoading: false,
  purchaseOrderCollection: null,
  purchaseOrderCollectionError: null,
  // purchase order model
  purchaseOrderModelLoading: false,
  purchaseOrderModel: null,
  purchaseOrderModelError: null,
  // purchase order collection
  purchaseOrderStoreCollectionLoading: false,
  purchaseOrderStoreCollection: null,
  purchaseOrderStoreCollectionError: null,
  // purchase order reco model
  purchaseOrderRecoModelLoading: false,
  purchaseOrderRecoModel: null,
  purchaseOrderRecoModelError: null,
  purchaseOrderRecoModelProducts: [],
  // purchase order reco basis model
  purchaseOrderRecoBasisModelLoading: false,
  purchaseOrderRecoBasisModel: null,
  purchaseOrderRecoBasisModelError: null,
  // purchase order outstanding payables model
  purchaseOrderOutstandingPayablesModelLoading: false,
  purchaseOrderOutstandingPayablesModel: null,
  purchaseOrderOutstandingPayablesModelError: null,
  // purchase order reco store collection
  purchaseOrderRecoStoreCollectionLoading: false,
  purchaseOrderRecoStoreCollection: null,
  purchaseOrderRecoStoreCollectionError: null,
};

export const purchaseOrderSlice = createSlice({
  name: 'purchaseOrder',
  initialState,
  reducers: {
    // purchase order collection
    setPurchaseOrderCollectionLoading(state, action: PayloadAction<boolean>) {
      state.purchaseOrderCollectionLoading = action.payload;
    },
    setPurchaseOrderCollection(state, action: PayloadAction<IPurchaseOrderCollection | null>) {
      state.purchaseOrderCollection = action.payload;
    },
    setPurchaseOrderCollectionError(state, action: PayloadAction<IError | null>) {
      state.purchaseOrderCollectionError = action.payload;
    },
    // purchase order model
    setPurchaseOrderModelLoading(state, action: PayloadAction<boolean>) {
      state.purchaseOrderModelLoading = action.payload;
    },
    setPurchaseOrderModel(state, action: PayloadAction<IPurchaseOrderModel | null>) {
      state.purchaseOrderModel = action.payload;
    },
    setPurchaseOrderModelError(state, action: PayloadAction<IError | null>) {
      state.purchaseOrderModelError = action.payload;
    },
    // purchase order store collection
    setPurchaseOrderStoreCollectionLoading(state, action: PayloadAction<boolean>) {
      state.purchaseOrderStoreCollectionLoading = action.payload;
    },
    setPurchaseOrderStoreCollection(state, action: PayloadAction<IPurchaseOrderStoreCollection | null>) {
      state.purchaseOrderStoreCollection = action.payload;
    },
    setPurchaseOrderStoreCollectionError(state, action: PayloadAction<IError | null>) {
      state.purchaseOrderStoreCollectionError = action.payload;
    },
    // purchase order reco model
    setPurchaseOrderRecoModelLoading(state, action: PayloadAction<boolean>) {
      state.purchaseOrderRecoModelLoading = action.payload;
    },
    setPurchaseOrderRecoModel(state, action: PayloadAction<IPurchaseOrderRecoModel | null>) {
      state.purchaseOrderRecoModel = action.payload;
    },
    setPurchaseOrderRecoModelError(state, action: PayloadAction<IError | null>) {
      state.purchaseOrderRecoModelError = action.payload;
    },
    setPurchaseOrderRecoModelProducts(state, action: PayloadAction<IPurchaseOrderRecoProduct[]>) {
      state.purchaseOrderRecoModelProducts = action.payload;
    },
    // purchase order reco basis model
    setPurchaseOrderRecoBasisModelLoading(state, action: PayloadAction<boolean>) {
      state.purchaseOrderRecoBasisModelLoading = action.payload;
    },
    setPurchaseOrderRecoBasisModel(state, action: PayloadAction<IPurchaseOrderRecoBasisModel | null>) {
      state.purchaseOrderRecoBasisModel = action.payload;
    },
    setPurchaseOrderRecoBasisModelError(state, action: PayloadAction<IError | null>) {
      state.purchaseOrderRecoBasisModelError = action.payload;
    },
    // purchase order outstanding payables model
    setPurchaseOrderOutstandingPayablesModelLoading(state, action: PayloadAction<boolean>) {
      state.purchaseOrderOutstandingPayablesModelLoading = action.payload;
    },
    setPurchaseOrderOutstandingPayablesModel(
      state,
      action: PayloadAction<IPurchaseOrderOutstandingPayablesModel | null>
    ) {
      state.purchaseOrderOutstandingPayablesModel = action.payload;
    },
    setPurchaseOrderOutstandingPayablesModelError(state, action: PayloadAction<IError | null>) {
      state.purchaseOrderOutstandingPayablesModelError = action.payload;
    },
    // purchase order reco store collection
    setPurchaseOrderRecoStoreCollectionLoading(state, action: PayloadAction<boolean>) {
      state.purchaseOrderRecoStoreCollectionLoading = action.payload;
    },
    setPurchaseOrderRecoStoreCollection(state, action: PayloadAction<IPurchaseOrderRecoStoreCollection | null>) {
      state.purchaseOrderRecoStoreCollection = action.payload;
    },
    setPurchaseOrderRecoStoreCollectionError(state, action: PayloadAction<IError | null>) {
      state.purchaseOrderRecoStoreCollectionError = action.payload;
    },
  },
});

export const {
  // purchase order collection
  setPurchaseOrderCollectionLoading,
  setPurchaseOrderCollection,
  setPurchaseOrderCollectionError,
  // purchase order model
  setPurchaseOrderModelLoading,
  setPurchaseOrderModel,
  setPurchaseOrderModelError,
  // purchase order store collection
  setPurchaseOrderStoreCollectionLoading,
  setPurchaseOrderStoreCollection,
  setPurchaseOrderStoreCollectionError,
  // purchase order reco model
  setPurchaseOrderRecoModelLoading,
  setPurchaseOrderRecoModel,
  setPurchaseOrderRecoModelError,
  setPurchaseOrderRecoModelProducts,
  // purchase order reco basis model
  setPurchaseOrderRecoBasisModelLoading,
  setPurchaseOrderRecoBasisModel,
  setPurchaseOrderRecoBasisModelError,
  // purchase order outstanding payables model
  setPurchaseOrderOutstandingPayablesModelLoading,
  setPurchaseOrderOutstandingPayablesModel,
  setPurchaseOrderOutstandingPayablesModelError,
  // purchase order reco store collection
  setPurchaseOrderRecoStoreCollectionLoading,
  setPurchaseOrderRecoStoreCollection,
  setPurchaseOrderRecoStoreCollectionError,
} = purchaseOrderSlice.actions;
export default purchaseOrderSlice.reducer;

export const getPurchaseOrderCollection = (params: IPurchaseOrderCollectionParams) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setPurchaseOrderCollectionLoading(true));

    try {
      const purchaseOrderModel = await purchaseOrderTransport.getCollection(params);

      dispatch(setPurchaseOrderCollection(purchaseOrderModel));
    } catch (error) {
      const _error = error as IError;

      dispatch(setPurchaseOrderCollectionError(_error));
    } finally {
      dispatch(setPurchaseOrderCollectionLoading(false));
    }
  }

  return thunk;
};

export const getPurchaseOrderModelBySonum = (params: IPurchaseOrderModelParams) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setPurchaseOrderModelLoading(true));

    try {
      const purchaseOrderModel = await purchaseOrderTransport.getBySonum(params);

      dispatch(setPurchaseOrderModel(purchaseOrderModel));
    } catch (error) {
      const _error = error as IError;

      dispatch(setPurchaseOrderModelError(_error));
    } finally {
      dispatch(setPurchaseOrderModelLoading(false));
    }
  }

  return thunk;
};

export const createPurchaseOrderModel = (payload: IPurchaseOrderCreatePayload) => {
  // Returns the real outcome so the caller can avoid redirecting on a failed submit:
  // HTTP error → caught here; HTTP 200 with `success: false` (Leslie returned ERROR) → not an error.
  async function thunk(dispatch: AppDispatch): Promise<{ success: boolean; message?: string }> {
    dispatch(setPurchaseOrderModelLoading(true));

    try {
      const purchaseOrderModel = await purchaseOrderTransport.create(payload);

      dispatch(setPurchaseOrderModel(purchaseOrderModel));

      return { success: (purchaseOrderModel as { success?: boolean })?.success === true };
    } catch (error) {
      const _error = error as IError;

      dispatch(setPurchaseOrderModelError(_error));

      return { success: false, message: _error?.data?.message };
    } finally {
      dispatch(setPurchaseOrderModelLoading(false));
    }
  }

  return thunk;
};

export const getPurchaseOrderStoreCollection = () => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setPurchaseOrderStoreCollectionLoading(true));

    try {
      const purchaseOrderStoreCollection = await purchaseOrderTransport.getStoreCollection();

      dispatch(setPurchaseOrderStoreCollection(purchaseOrderStoreCollection));
    } catch (error) {
      const _error = error as IError;

      dispatch(setPurchaseOrderStoreCollectionError(_error));
    } finally {
      dispatch(setPurchaseOrderStoreCollectionLoading(false));
    }
  }

  return thunk;
};

export const getPurchaseOrderRecoModel = (params: IPurchaseOrderRecoParams) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setPurchaseOrderRecoModelLoading(true));

    try {
      const purchaseOrderRecoModel = await purchaseOrderTransport.getReco(params);

      dispatch(setPurchaseOrderRecoModel(purchaseOrderRecoModel));
    } catch (error) {
      const _error = error as IError;

      dispatch(setPurchaseOrderRecoModelError(_error));
    } finally {
      dispatch(setPurchaseOrderRecoModelLoading(false));
    }
  }

  return thunk;
};

export const getPurchaseOrderRecoBasisModel = (filters: IPurchaseOrderRecoBasisFilters) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setPurchaseOrderRecoBasisModelLoading(true));

    try {
      const purchaseOrderRecoBasisModel = await purchaseOrderTransport.getRecoBasis(filters);

      dispatch(setPurchaseOrderRecoBasisModel(purchaseOrderRecoBasisModel));
    } catch (error) {
      const _error = error as IError;

      dispatch(setPurchaseOrderRecoBasisModelError(_error));
    } finally {
      dispatch(setPurchaseOrderRecoBasisModelLoading(false));
    }
  }

  return thunk;
};

export const getPurchaseOrderOutstandingPayablesModel = (params?: { storeCode?: string }) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setPurchaseOrderOutstandingPayablesModelLoading(true));

    try {
      const purchaseOrderOutstandingPayablesModel = await purchaseOrderTransport.getOutstandingPayables(params);

      dispatch(setPurchaseOrderOutstandingPayablesModel(purchaseOrderOutstandingPayablesModel));
    } catch (error) {
      const _error = error as IError;

      dispatch(setPurchaseOrderOutstandingPayablesModelError(_error));
    } finally {
      dispatch(setPurchaseOrderOutstandingPayablesModelLoading(false));
    }
  }

  return thunk;
};

// MBC-85: Track PO RECO column.
export const getPurchaseOrderRecoStoreCollection = () => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setPurchaseOrderRecoStoreCollectionLoading(true));

    try {
      const recoStoreCollection = await purchaseOrderTransport.getRecoStoreCollection();
      dispatch(setPurchaseOrderRecoStoreCollection(recoStoreCollection));
    } catch (error) {
      dispatch(setPurchaseOrderRecoStoreCollectionError(error as IError));
    } finally {
      dispatch(setPurchaseOrderRecoStoreCollectionLoading(false));
    }
  }

  return thunk;
};
